import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import { RealtimeNotificationsProvider } from "@/components/RealtimeNotificationsProvider";
import { SettingsSlideover } from "@/components/SettingsSlideover";
import { AuthExpiredBanner } from "@/components/AuthExpiredBanner";
import { SocketStatusBanner } from "@/components/SocketStatusBanner";
import { CommandPalette } from "@/components/CommandPalette";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { Loader2 } from "lucide-react";
// Phase 20.1 — Auth + Register are the unauthenticated entry pages and
// must be eager-imported. Lazy-loading them caused React error 426 on
// the stale-token → 401 → ProtectedRoute → <Navigate to="/auth" />
// path: when auth state flipped from loading→unauthenticated, the lazy
// Auth chunk would suspend mid-bounce and the Suspense boundary tripped.
// Each chunk is ~3 KB; bundling them into the main bundle costs nothing
// noticeable and removes the entire class of bug.
import Auth from "./pages/Auth";
import Register from "./pages/Register";
import SsoLogin from "./pages/SsoLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

const Index = lazy(() => import("./pages/Index"));
const LiveStream = lazy(() => import("./pages/LiveStream"));
const Devices = lazy(() => import("./pages/Devices"));
const DeviceDetails = lazy(() => import("./pages/DeviceDetails"));
const Screenshots = lazy(() => import("./pages/Screenshots"));
const Productivity = lazy(() => import("./pages/Productivity"));
const Activity = lazy(() => import("./pages/Activity"));
const Timeline = lazy(() => import("./pages/Timeline"));
const Goals = lazy(() => import("./pages/Goals"));
const AppsUrls = lazy(() => import("./pages/AppsUrls"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Users = lazy(() => import("./pages/Users"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const Settings = lazy(() => import("./pages/Settings"));
const MobileApp = lazy(() => import("./pages/MobileApp"));
const AgentDownload = lazy(() => import("./pages/AgentDownload"));
const Install = lazy(() => import("./pages/Install"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const EnrollmentTokens = lazy(() => import("./pages/EnrollmentTokens"));
const Billing = lazy(() => import('./pages/Billing'));
const Reports = lazy(() => import("./pages/Reports"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Rules = lazy(() => import("./pages/Rules"));
const Ops = lazy(() => import("./pages/Ops"));
const GoalsPlatform = lazy(() => import("./pages/GoalsPlatform"));
const AuditExplorer = lazy(() => import("./pages/AuditExplorer"));
const OpsIngestion  = lazy(() => import("./pages/OpsIngestion"));
const AgentUpdates  = lazy(() => import("./pages/AgentUpdates"));
const LegacyAgents  = lazy(() => import("./pages/LegacyAgents"));
const Attendance    = lazy(() => import("./pages/Attendance"));
const ReportsLegacy = lazy(() => import("./pages/ReportsLegacy"));
const RulesAdvanced = lazy(() => import("./pages/RulesAdvanced"));
const Teams         = lazy(() => import("./pages/Teams"));
const Location      = lazy(() => import("./pages/Location")); // Phase 31 — GPS & geofencing
const Notifications = lazy(() => import("./pages/Notifications")); // Phase 33
const Invites       = lazy(() => import("./pages/Invites"));       // Phase 35
const InviteAccept  = lazy(() => import("./pages/InviteAccept"));  // Phase 35 (public)
const Integrations  = lazy(() => import("./pages/Integrations"));  // Phase 36
const AdminCenter   = lazy(() => import("./pages/AdminCenter"));   // Phase 2 — Admin Center
const Marketplace   = lazy(() => import("./pages/Marketplace"));   // Phase 2 — Marketplace

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function PageLoader() {
  return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
}

function SafePage({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary><Suspense fallback={<PageLoader />}>{children}</Suspense></ErrorBoundary>;
}

// Phase 12.2 — deep-link /settings into the slideover.
// Refreshing on /settings or pasting it as a URL used to render the
// 530-line Settings page full-screen, bypassing the "dashboard stays
// alive behind the panel" contract. We redirect to / with ?settings=1
// (and forward any other search params so deep-link state survives).
function SettingsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('settings', '1');
  return <Navigate to={{ pathname: '/', search: `?${params.toString()}` }} replace />;
}

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
              <RealtimeNotificationsProvider>
              <Routes>
                <Route path="/auth" element={<SafePage><Auth /></SafePage>} />
                <Route path="/login" element={<SafePage><Auth /></SafePage>} />
                <Route path="/sso/login" element={<SafePage><SsoLogin /></SafePage>} />
                <Route path="/forgot-password" element={<SafePage><ForgotPassword /></SafePage>} />
                <Route path="/reset-password" element={<SafePage><ResetPassword /></SafePage>} />
                <Route path="/register" element={<SafePage><Register /></SafePage>} />
                <Route path="/install" element={<SafePage><Install /></SafePage>} />
                {/* Phase 35 — public invite acceptance (no auth; the token is the credential). */}
                <Route path="/invite/:token" element={<SafePage><InviteAccept /></SafePage>} />
                <Route path="/" element={<ProtectedRoute><SafePage><Index /></SafePage></ProtectedRoute>} />
                {/* Phase 31 BUG 1 — LiveStream is lazy() but was the only route
                    missing the <SafePage> Suspense boundary. Navigating to it
                    (e.g. the sidebar link) suspended with no boundary → React
                    error #426. Wrapping it matches every other route. */}
                <Route path="/live-stream" element={<ProtectedRoute><SafePage><LiveStream /></SafePage></ProtectedRoute>} />
              <Route path="/devices" element={<ProtectedRoute><SafePage><Devices /></SafePage></ProtectedRoute>} />
                <Route path="/devices/:id" element={<ProtectedRoute><SafePage><DeviceDetails /></SafePage></ProtectedRoute>} />
                <Route path="/screenshots" element={<ProtectedRoute><SafePage><Screenshots /></SafePage></ProtectedRoute>} />
                <Route path="/productivity" element={<ProtectedRoute><SafePage><Productivity /></SafePage></ProtectedRoute>} />
                <Route path="/activity" element={<ProtectedRoute><SafePage><Activity /></SafePage></ProtectedRoute>} />
                <Route path="/timeline" element={<ProtectedRoute><SafePage><Timeline /></SafePage></ProtectedRoute>} />
                <Route path="/goals" element={<ProtectedRoute><SafePage><Goals /></SafePage></ProtectedRoute>} />
                <Route path="/apps-urls" element={<ProtectedRoute><SafePage><AppsUrls /></SafePage></ProtectedRoute>} />
                <Route path="/sessions" element={<ProtectedRoute><SafePage><Sessions /></SafePage></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute><SafePage><Users /></SafePage></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><SafePage><AdminCenter /></SafePage></ProtectedRoute>} />
                <Route path="/marketplace" element={<ProtectedRoute><SafePage><Marketplace /></SafePage></ProtectedRoute>} />
                <Route path="/audit" element={<ProtectedRoute><SafePage><AuditLogs /></SafePage></ProtectedRoute>} />
                {/* Phase 12.2 — /settings deep-links into the slideover instead of
                    rendering the page full-screen. The full-page route was a
                    legacy bypass that broke the "preserve dashboard context"
                    contract documented above. */}
                <Route path="/settings" element={<SettingsRedirect />} />
                <Route path="/agent-download" element={<ProtectedRoute><SafePage><AgentDownload /></SafePage></ProtectedRoute>} />
            <Route path="/mobile-app" element={<ProtectedRoute><SafePage><MobileApp /></SafePage></ProtectedRoute>} />
                <Route path="/super-admin" element={<ProtectedRoute><SafePage><SuperAdmin /></SafePage></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><SafePage><Billing /></SafePage></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><SafePage><Reports /></SafePage></ProtectedRoute>} />
                <Route path="/enrollment-tokens" element={<ProtectedRoute><SafePage><EnrollmentTokens /></SafePage></ProtectedRoute>} />
                <Route path="/rules" element={<ProtectedRoute><SafePage><Rules /></SafePage></ProtectedRoute>} />
                {/* Phase 29 — Phase 9 scope-rule engine preserved alongside the new
                    classification/scoring/alert Rules page. */}
                <Route path="/rules-advanced" element={<ProtectedRoute><SafePage><RulesAdvanced /></SafePage></ProtectedRoute>} />
                <Route path="/ops"   element={<ProtectedRoute><SafePage><Ops   /></SafePage></ProtectedRoute>} />
                <Route path="/ops/ingestion" element={<ProtectedRoute><SafePage><OpsIngestion /></SafePage></ProtectedRoute>} />
                <Route path="/agent-updates" element={<ProtectedRoute><SafePage><AgentUpdates /></SafePage></ProtectedRoute>} />
                <Route path="/legacy-agents" element={<ProtectedRoute><SafePage><LegacyAgents /></SafePage></ProtectedRoute>} />
                <Route path="/goals-platform" element={<ProtectedRoute><SafePage><GoalsPlatform /></SafePage></ProtectedRoute>} />
                <Route path="/audit-explorer" element={<ProtectedRoute><SafePage><AuditExplorer /></SafePage></ProtectedRoute>} />
                <Route path="/attendance" element={<ProtectedRoute><SafePage><Attendance /></SafePage></ProtectedRoute>} />
                <Route path="/teams" element={<ProtectedRoute><SafePage><Teams /></SafePage></ProtectedRoute>} />
                <Route path="/location" element={<ProtectedRoute><SafePage><Location /></SafePage></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><SafePage><Notifications /></SafePage></ProtectedRoute>} />
                <Route path="/invites" element={<ProtectedRoute><SafePage><Invites /></SafePage></ProtectedRoute>} />{/* Phase 35 */}
                <Route path="/integrations" element={<ProtectedRoute><SafePage><Integrations /></SafePage></ProtectedRoute>} />{/* Phase 36 */}
                {/* Phase 28 — legacy /api/reports/* report hub, preserved while the
                    new Hubstaff-style /reports is built out report-by-report. */}
                <Route path="/reports-legacy" element={<ProtectedRoute><SafePage><ReportsLegacy /></SafePage></ProtectedRoute>} />
                <Route path="*" element={<SafePage><NotFound /></SafePage>} />
              </Routes>
              {/* Global slideover — opens via ?settings=1 on any route */}
              <SettingsSlideover />
              {/* Phase 14 — Cmd+K command palette */}
              <CommandPalette />
              {/* Global auth-expired surface — listens to api-client 401s */}
              <AuthExpiredBanner />
              {/* Global socket-status surface — singleton socket reconnect UX */}
              <SocketStatusBanner />
              {/* Phase 34 — premium polish: offline banner + first-run onboarding */}
              <OfflineIndicator />
              <OnboardingWizard />
              </RealtimeNotificationsProvider>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
